import React, { useEffect, useState } from 'react';
import {
    Box, Button, Card, Chip, CircularProgress, MenuItem, TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import theme from "../../../assets/theme";
import { SupportController } from "../../../controllers/supportController";

const STATUSES = ["OPEN", "IN_PROGRESS", "WAITING", "RESOLVED", "CLOSED"];

const statusColor = (s: string) =>
    s === "RESOLVED" || s === "CLOSED" ? "success" : s === "WAITING" ? "warning" : "info";

// Merchant-facing inbox of customer tickets about THIS merchant's products.
// Backed by GET /support/tickets/merchant-inbox (merchantId == caller).
function CustomerTickets() {
    const { typography } = theme;
    const [tickets, setTickets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState<any | null>(null);
    const [reply, setReply] = useState("");
    const [busy, setBusy] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const res: any = await SupportController.getMerchantInbox({ page: 0, size: 50, status: statusFilter });
            setTickets(res?.data?.data?.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

    const openThread = async (id: string) => {
        try {
            const res: any = await SupportController.getTicket(id);
            setActive(res?.data?.data);
        } catch (e: any) {
            toast.error(e?.response?.data?.errors?.[0] || "Failed to open ticket");
        }
    };

    const sendReply = async () => {
        if (!reply.trim()) return;
        setBusy(true);
        try {
            const res: any = await SupportController.reply(active.id, reply.trim());
            setActive(res?.data?.data);
            setReply("");
            load();
        } catch (e: any) {
            toast.error(e?.response?.data?.errors?.[0] || "Failed to send reply");
        } finally {
            setBusy(false);
        }
    };

    const changeStatus = async (status: string) => {
        setBusy(true);
        try {
            const res: any = await SupportController.updateStatus(active.id, status);
            setActive(res?.data?.data);
            toast.success(`Ticket marked ${status.toLowerCase()}`);
            load();
        } catch (e: any) {
            toast.error(e?.response?.data?.errors?.[0] || "Failed to update status");
        } finally {
            setBusy(false);
        }
    };

    const escalate = async () => {
        setBusy(true);
        try {
            const res: any = await SupportController.escalate(active.id);
            setActive(res?.data?.data);
            toast.success("Escalated to platform support");
            load();
        } catch (e: any) {
            toast.error(e?.response?.data?.errors?.[0] || "Failed to escalate");
        } finally {
            setBusy(false);
        }
    };

    // ----- thread view -----
    if (active) {
        return (
            <FaiBox className={"order"}>
                <Box className="flex justify-between items-center" mb={2}>
                    <Button onClick={() => setActive(null)}>← Back to inbox</Button>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        {active.escalated && <Chip label="Escalated" color="warning" size="small" />}
                        <Chip label={active.status} color={statusColor(active.status) as any} size="small" />
                    </Box>
                </Box>
                <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium}>
                    {active.subject}
                </FaiTypography>
                <FaiTypography variant={"caption"}>
                    {active.ticketNumber} · {active.category}
                    {active.subOrderId ? ` · order ${active.subOrderId}` : ""}
                </FaiTypography>

                <Card sx={{ p: 2, mt: 2, maxHeight: 420, overflowY: "auto" }}>
                    {(active.messages || []).map((m: any, i: number) => {
                        const mine = m.authorRole === "ROLE_MERCHANT";
                        return (
                            <Box key={i} sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", mb: 1.5 }}>
                                <Box sx={{ maxWidth: "70%", p: 1.5, borderRadius: 2, bgcolor: mine ? "#E09145" : "#F0F0F0", color: mine ? "#fff" : "#000" }}>
                                    <FaiTypography variant={"caption"} sx={{ fontWeight: 600 }}>
                                        {mine ? "You (seller)" : m.authorRole === "ROLE_ADMIN" ? "Platform support" : "Customer"}
                                    </FaiTypography>
                                    <FaiTypography variant={"body2"}>{m.body}</FaiTypography>
                                </Box>
                            </Box>
                        );
                    })}
                </Card>

                {active.status !== "CLOSED" && (
                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                        <TextField fullWidth size="small" placeholder="Reply to the customer…" value={reply} onChange={(e) => setReply(e.target.value)} />
                        <Button variant="contained" disabled={busy} onClick={sendReply}>Send</Button>
                    </Box>
                )}

                <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                    {active.status !== "RESOLVED" && active.status !== "CLOSED" && (
                        <Button variant="outlined" color="success" disabled={busy} onClick={() => changeStatus("RESOLVED")}>
                            Mark resolved
                        </Button>
                    )}
                    {active.status !== "CLOSED" && (
                        <Button variant="outlined" disabled={busy} onClick={() => changeStatus("CLOSED")}>
                            Close
                        </Button>
                    )}
                    {!active.escalated && active.status !== "CLOSED" && (
                        <Button variant="outlined" color="warning" disabled={busy} onClick={escalate}>
                            Escalate to platform
                        </Button>
                    )}
                </Box>
            </FaiBox>
        );
    }

    // ----- list view -----
    return (
        <FaiBox className={"order"}>
            <Box className="flex justify-between items-center" mb={"21px"}>
                <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium}>
                    Customer Tickets
                </FaiTypography>
                <TextField select size="small" label="Status" value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 180 }}>
                    <MenuItem value="">All statuses</MenuItem>
                    {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
            </Box>

            {loading ? (
                <Box className="flex justify-center p-6"><CircularProgress /></Box>
            ) : tickets.length === 0 ? (
                <FaiTypography>No customer tickets yet.</FaiTypography>
            ) : (
                tickets.map((t) => (
                    <Card key={t.id} sx={{ p: 2, mb: 1.5, cursor: "pointer" }} onClick={() => openThread(t.id)}>
                        <Box className="flex justify-between items-center">
                            <Box>
                                <FaiTypography variant={"body1"} sx={{ fontWeight: 600 }}>{t.subject}</FaiTypography>
                                <FaiTypography variant={"caption"}>{t.ticketNumber} · {t.category}</FaiTypography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                {t.escalated && <Chip label="Escalated" color="warning" size="small" />}
                                <Chip label={t.status} color={statusColor(t.status) as any} size="small" />
                            </Box>
                        </Box>
                    </Card>
                ))
            )}
        </FaiBox>
    );
}

export default CustomerTickets;
